/**
 * The chrome every credential screen shares: the deep-green ground, the
 * wordmark with the "for Aspira" rule, and the white card under it.
 *
 * It is the rail's header treatment (artifacts/lib-rr-base.mjs, `.rr-sb .hd`)
 * lifted onto a full page, so the first thing anyone sees is recognisably the
 * same product as the thing behind the login — not a bare form on white.
 */

import type { ReactNode } from "react";
import "./auth.css";

export function AuthShell({
  eyebrow,
  title,
  sub,
  children,
  foot,
  wide,
}: {
  eyebrow: string;
  title: string;
  sub?: ReactNode;
  children: ReactNode;
  foot?: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className="rra-page">
      <div className={`rra-frame${wide ? " rra-frame-wide" : ""}`}>
        <div className="rra-brand">
          {/* The light mark, on the green chrome — decoded from the artifact's
              brand JSON at build time by web/scripts/build-brand.mjs. */}
          <img src="/brand/rr-wordmark-light.png" alt="RocketRep" />
          <div className="rra-for">
            <i />
            for
            <i />
          </div>
          {/* ★ Aspira's own mark (Edward, 1 Oct), on a white tile with rounded
              corners: their logo is drawn for a light ground, not this green. */}
          <span className="rra-client">
            <img src="/brand/aspira-logo.png" alt="Aspira Pathlab &amp; Diagnostics" width={92} height={34} loading="eager" decoding="sync" />
          </span>
        </div>

        <div className="rra-card">
          {/* ★ the single use of the mint→periwinkle gradient on this screen */}
          <i className="rra-accent" aria-hidden="true" />
          <div className="rra-in">
            <div className="rra-eyebrow">{eyebrow}</div>
            <h1 className="rra-h">{title}</h1>
            {sub ? <p className="rra-sub">{sub}</p> : null}
            {children}
          </div>
        </div>

        {foot ? <div className="rra-foot">{foot}</div> : null}
      </div>
    </div>
  );
}

/**
 * The `checking` ground.
 *
 * Empty for the first fifth of a second, then a single line. The common case is
 * a signed-in person refreshing, and for them this is a blink; the line is for
 * the rep on a two-bar connection who would otherwise be looking at nothing.
 */
export function Booting({ label = "Checking your session" }: { label?: string }) {
  return (
    <div className="rra-boot">
      <div className="rra-boot-t">{label}…</div>
    </div>
  );
}

/** The submit button, with its own in-flight state. */
export function Submit({
  busy,
  disabled,
  children,
  busyLabel,
}: {
  busy: boolean;
  disabled?: boolean;
  children: ReactNode;
  busyLabel: string;
}) {
  return (
    <button type="submit" className="rra-cta" disabled={busy || disabled}>
      {busy ? <i className="rra-spin" aria-hidden="true" /> : null}
      {busy ? busyLabel : children}
    </button>
  );
}

/** A message panel. `tone` picks the left rule; the body is free prose. */
export function Message({
  tone = "neutral",
  heading,
  children,
}: {
  tone?: "neutral" | "bad" | "ok";
  heading?: string;
  children: ReactNode;
}) {
  const cls =
    tone === "bad" ? "rra-msg rra-msg-bad" : tone === "ok" ? "rra-msg rra-msg-ok" : "rra-msg";
  return (
    <div className={cls} role={tone === "bad" ? "alert" : "status"}>
      {heading ? <b className="rra-msg-h">{heading}</b> : null}
      {children}
    </div>
  );
}
